import { toHtml } from '@/pocs/chat/export/formats/html'
import { toJson } from '@/pocs/chat/export/formats/json'
import { toMarkdown } from '@/pocs/chat/export/formats/markdown'
import { toPlainText } from '@/pocs/chat/export/formats/txt'
import { MIME_TYPES } from '@deepcloud/deep-ui-utils'
import type {
  ChatExportFile,
  ChatExportFormat,
  ChatTranscript,
} from '@/pocs/chat/export/types'

interface ChatExportFormatDefinition {
  id: ChatExportFormat
  extension: string
  mimeType: string
  render: (transcript: ChatTranscript) => Promise<ChatExportFile>
  // DeepBox converts DOCX to PDF server-side; the uploaded DOCX is removed afterwards
  convertToPdf: boolean
}

function textFormat(
  id: ChatExportFormat,
  extension: string,
  mimeType: string,
  serialize: (transcript: ChatTranscript) => string,
): ChatExportFormatDefinition {
  return {
    id,
    extension,
    mimeType,
    convertToPdf: false,
    render: async (transcript) => ({
      blob: new Blob([serialize(transcript)], {
        type: `${mimeType};charset=utf-8`,
      }),
      extension,
    }),
  }
}

async function renderDocx(transcript: ChatTranscript): Promise<ChatExportFile> {
  // the docx library is large, so it is only loaded once a Word/PDF export runs
  const { toDocx } = await import('@/pocs/chat/export/formats/docx')
  const blob = await toDocx(transcript)
  return {
    blob: new Blob([blob], { type: MIME_TYPES.MIME_TYPE_DOCX }),
    extension: 'docx',
  }
}

export const CHAT_EXPORT_FORMATS: ChatExportFormatDefinition[] = [
  {
    id: 'pdf',
    extension: 'pdf',
    mimeType: MIME_TYPES.MIME_TYPE_PDF,
    render: renderDocx,
    convertToPdf: true,
  },
  textFormat('markdown', 'md', MIME_TYPES.MIME_TYPE_MARKDOWN, toMarkdown),
  textFormat('txt', 'txt', MIME_TYPES.MIME_TYPE_TXT, toPlainText),
  textFormat('json', 'json', MIME_TYPES.MIME_TYPE_JSON, toJson),
  textFormat('html', 'html', MIME_TYPES.MIME_TYPE_HTML, toHtml),
  {
    id: 'docx',
    extension: 'docx',
    mimeType: MIME_TYPES.MIME_TYPE_DOCX,
    render: renderDocx,
    convertToPdf: false,
  },
]

export const DEFAULT_CHAT_EXPORT_FORMAT: ChatExportFormat = 'pdf'

export function getChatExportFormat(id: ChatExportFormat) {
  const format = CHAT_EXPORT_FORMATS.find((definition) => definition.id === id)
  if (!format) {
    throw new Error(`Unknown chat export format "${id}"`)
  }
  return format
}
